<script setup lang="ts">
/**
 * A chart section, in two tabs: its figures (its name, its type, the grid of
 * figures and their colours) and how it is drawn (bars, figures, scale,
 * legend, unit, height and caption). Both are `ChartFields`, shared with a
 * chart placed freely on a page.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import ChartFields from '@/components/charts/ChartFields.vue'
import { useRememberedTab } from '@/composables/useRememberedTab'
import type { ChartContent } from '@/domain/chart'
import type { ChartSection } from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'
import InspectorTabs from '@/sections/InspectorTabs.vue'
import { TAB_ICONS, type InspectorTab } from '@/sections/inspectorTabs'

const { t } = useI18n()

const props = defineProps<{ section: ChartSection }>()
const emit = defineEmits<{ change: [section: ChartSection] }>()

type ChartTab = 'data' | 'look'

const tab = useRememberedTab<ChartTab>('chart', ['data', 'look'])

const tabs = computed<InspectorTab<ChartTab>[]>(() => [
  { id: 'data', label: t('inspector.chart.tabs.data'), icon: TAB_ICONS.data },
  { id: 'look', label: t('inspector.chart.tabs.look'), icon: TAB_ICONS.look },
])

function set(changes: Partial<ChartSection> | Partial<ChartContent>): void {
  emit('change', { ...props.section, ...changes })
}
</script>

<template>
  <InspectorTabs v-model:open="tab" :tabs="tabs" :label="t('inspector.chart.tabs.label')">
    <template #data>
      <div class="fields">
        <InspectorField :label="t('inspector.chart.title')">
          <input
            class="b-input"
            :value="section.title"
            @input="set({ title: ($event.target as HTMLInputElement).value })"
          />
        </InspectorField>

        <ChartFields :chart="section" part="data" @change="set" />
      </div>
    </template>

    <template #look>
      <div class="fields">
        <ChartFields :chart="section" part="look" @change="set" />

        <InspectorField
          :label="t('inspector.chart.height')"
          :hint="t('inspector.chart.heightHint', { mm: section.height })"
        >
          <input
            class="range"
            type="range"
            min="40"
            max="140"
            step="5"
            :value="section.height"
            @input="set({ height: Number(($event.target as HTMLInputElement).value) })"
          />
        </InspectorField>

        <InspectorField :label="t('inspector.chart.caption')">
          <input
            class="b-input"
            :value="section.caption"
            @input="set({ caption: ($event.target as HTMLInputElement).value })"
          />
        </InspectorField>
      </div>
    </template>
  </InspectorTabs>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.range {
  width: 100%;
  accent-color: var(--accent);
}
</style>
